import {
  IconFileText,
  IconPound,
  IconRefreshCcw,
  IconShieldCheck,
  IconTimesDefault,
} from '@iwoca/icons';
import { clsx } from 'clsx';

import { PillConfig, TabConfig } from '../../CustomersV2.types';
import { TableSettings } from '../../hooks/useTableSettings';
import { trackFilterClicked } from '../../tracking';

const iconFor = (icon: PillConfig['icon']) => {
  switch (icon) {
    case 'file':
      return IconFileText;
    case 'shield':
      return IconShieldCheck;
    case 'declined':
      return IconTimesDefault;
    case 'pound':
      return IconPound;
    case 'refresh':
      return IconRefreshCcw;
  }
};

export const CustomersToolbar = ({
  config,
  activeFilter,
  counts,
  settings,
  onFilterChange,
  rightSlot,
}: {
  config: TabConfig;
  activeFilter: string | null;
  counts: Record<string, number> | undefined;
  settings: TableSettings;
  onFilterChange: (value: string | null) => void;
  rightSlot?: React.ReactNode;
}) => {
  return (
    <div className="flex items-center justify-between gap-l max-1024:flex-col max-1024:items-stretch">
      <div className="flex flex-wrap gap-l max-1024:flex-nowrap max-1024:overflow-x-auto max-1024:[-ms-overflow-style:none] max-1024:[scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {config.pills
          .filter((pill) => !pill.revealedBy || settings[pill.revealedBy])
          .map((pill) => {
            const Icon = iconFor(pill.icon);
            const isActive = activeFilter === pill.value;
            return (
              <button
                key={pill.value}
                type="button"
                aria-pressed={isActive}
                onClick={() => {
                  // Clearing an active pill is still a filter interaction — it's
                  // V2's nearest equivalent to V1's "all limits" button — so the
                  // pill's own value is sent either way and `selected` says which
                  // direction it went.
                  trackFilterClicked({
                    tab: config.path,
                    filterName: pill.value,
                    selected: !isActive,
                  });
                  onFilterChange(isActive ? null : pill.value);
                }}
                className={clsx(
                  'flex shrink-0 cursor-pointer items-center gap-s whitespace-nowrap rounded-2xl border-none px-m py-s text-m font-med text-secondary-40',
                  isActive ? 'bg-secondary-80' : 'bg-secondary-95',
                )}
              >
                <Icon height={20} width={20} />
                {/* No count while the totals are unknown, rather than a `(0)`
                    that corrects itself a moment later — or worse, sits beside a
                    full table after the request failed. Once they are known, a
                    stage the API omitted has genuinely zero rows. */}
                {counts
                  ? `${pill.label} (${counts[pill.value] ?? 0})`
                  : pill.label}
              </button>
            );
          })}
      </div>
      {rightSlot && <div className="max-1024:self-start">{rightSlot}</div>}
    </div>
  );
};
